/**
 * LiveActivityTicker — horizontal scrolling recent wins.
 *
 * Polls `GET /api/wins/live?limit=25` every 10 s (public, unauthenticated).
 * Backend caches for 5 s so N pollers = 1 Mongo query / 5 s.
 *
 * Robustness:
 *   - If the fetch fails we keep the last-known items and quietly log —
 *     the ticker is a marketing surface, never surface a UI error.
 *   - On first paint (no data yet) we render a low-key "Live wins loading…"
 *     stub instead of a jumping empty strip.
 *   - Items are keyed by round_id so new wins fade in at the front without
 *     re-mounting the whole strip.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { TrendingUp } from "lucide-react";

import { BACKEND_URL } from "../../lib/api";
import { formatAmount } from "../../lib/currency";

const POLL_MS = 10_000;
const FETCH_LIMIT = 25;

async function fetchWins(signal) {
  const url = `${BACKEND_URL}/api/wins/live?limit=${FETCH_LIMIT}`;
  const r = await fetch(url, { signal, credentials: "omit" });
  if (!r.ok) throw new Error(`live wins HTTP ${r.status}`);
  const data = await r.json();
  return Array.isArray(data?.wins) ? data.wins : [];
}

export default function LiveActivityTicker({ className = "" }) {
  const [wins, setWins] = useState([]);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const ctrl = new AbortController();

    const tick = async () => {
      try {
        const next = await fetchWins(ctrl.signal);
        if (!alive.current) return;
        if (next.length > 0) setWins(next);
      } catch (e) {
        // Silent — keep previous items on the strip. Only log for debugging.
        if (e?.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.debug("[LiveActivityTicker] fetch failed:", e?.message || e);
        }
      }
    };

    tick();
    const interval = setInterval(tick, POLL_MS);
    return () => {
      alive.current = false;
      ctrl.abort();
      clearInterval(interval);
    };
  }, []);

  // Duplicate list so the CSS `translateX(-50%)` loop stays seamless. If
  // we have fewer than 4 items the loop looks janky; pad from a small
  // rotation of the fetched wins to keep motion smooth.
  const loop = useMemo(() => {
    if (wins.length === 0) return [];
    const rows = wins.length < 6 ? [...wins, ...wins, ...wins, ...wins] : [...wins, ...wins];
    return rows;
  }, [wins]);

  return (
    <div
      className={`relative flex items-stretch bg-elevated/80 backdrop-blur border-y border-line ${className}`}
      data-testid="live-activity-ticker"
    >
      {/* Pinned label — doesn't scroll */}
      <div className="flex items-center gap-2 shrink-0 px-4 py-2 bg-elevated border-r border-line z-10">
        <TrendingUp className="w-3 h-3 text-success" />
        <span className="text-[10px] tracking-[0.3em] uppercase text-ink-2 whitespace-nowrap">Live wins</span>
      </div>
      {/* Scrolling items */}
      <div className="flex-1 min-w-0 overflow-hidden py-2">
        {loop.length === 0 ? (
          <div className="flex items-center h-full px-4 text-[11px] tracking-[0.25em] uppercase text-ink-3">
            Live wins loading…
          </div>
        ) : (
          <div
            className="flex items-center gap-3 whitespace-nowrap animate-ticker hover:[animation-play-state:paused]"
            data-testid="live-activity-strip"
          >
            {loop.map((w, i) => (
              <span
                key={`${w.id}-${i}`}
                className="flex items-center gap-2 text-xs text-ink-2 shrink-0 pr-6 pl-3"
                data-testid={i < wins.length ? `live-win-${w.id}` : undefined}
              >
                <span className="text-ink">{w.username_masked}</span>
                <span className="text-ink-3">won</span>
                <span className="font-mono text-gold">
                  {formatAmount(w.payout, w.currency || "USDT")}
                </span>
                <span className="text-ink-3">on</span>
                <span className="text-pink">{w.game_name}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
